import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, ModeBadge, Empty, Btn, Input } from "@/components/kit";
import { Building2, Users, FileText, Cpu, DollarSign, Shield, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdmin() {
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [audit, setAudit] = useState([]);
  const [showReset, setShowReset] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);

  const loadOverview = () => {
    client.get("/admin/overview").then((r) => setO(r.data));
    client.get("/admin/audit").then((r) => setAudit(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!user?.is_super_admin) return;
    loadOverview();
  }, [user]);

  const runReset = async () => {
    setResetting(true);
    try {
      const { data } = await client.post("/admin/reset-data", { confirm: confirmText });
      toast.success(data.message || "Sistema zerado.");
      setShowReset(false);
      setConfirmText("");
      loadOverview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível zerar o sistema.");
    } finally {
      setResetting(false);
    }
  };

  if (!user?.is_super_admin)
    return <div><PageHeader title="Painel Super Admin" /><Empty>Acesso restrito ao Super Admin. Entre com admin@postflow.ai.</Empty></div>;

  const kpis = o ? [
    { icon: Building2, label: "Organizações", value: o.orgs, tint: "bg-blue-50 text-[#3457d5]" },
    { icon: Users, label: "Usuários", value: o.users, tint: "bg-indigo-50 text-indigo-600" },
    { icon: FileText, label: "Conteúdos", value: o.content, tint: "bg-emerald-50 text-emerald-600" },
    { icon: Cpu, label: "Tokens IA", value: o.ai_tokens, tint: "bg-purple-50 text-purple-600" },
    { icon: DollarSign, label: "Custo IA (US$)", value: o.ai_cost_usd, tint: "bg-amber-50 text-amber-600" },
    { icon: Building2, label: "Marcas", value: o.brands, tint: "bg-sky-50 text-sky-600" },
  ] : [];

  return (
    <div>
      <PageHeader title="Painel Super Admin" subtitle="Consumo, custos de IA, planos, providers e auditoria global."
        actions={<span className="inline-flex items-center gap-1.5 text-sm text-slate-500"><Shield size={16} className="text-[#3457d5]" /> {user.email}</span>} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {kpis.map((k) => (
          <Card key={k.label} className="flex items-center gap-3" data-testid={`admin-kpi-${k.label}`}>
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${k.tint}`}><k.icon size={18} /></div>
            <div><div className="text-xl font-extrabold font-display">{k.value}</div><div className="text-[10px] text-slate-500 uppercase">{k.label}</div></div>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-0 overflow-hidden">
          <div className="p-5 pb-3"><h3 className="font-display font-bold">Organizações (multi-tenant)</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-5 py-2">Nome</th><th className="text-left px-3 py-2">Plano</th><th className="px-3 py-2">Marcas</th><th className="px-3 py-2">Conteúdos</th><th className="px-3 py-2">Membros</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {o?.organizations?.map((org) => (
                  <tr key={org.id} data-testid={`admin-org-${org.id}`}><td className="px-5 py-2 font-medium text-slate-800">{org.name}</td><td className="px-3 py-2"><span className="text-xs bg-slate-100 rounded-full px-2 py-0.5">{org.plan}</span></td><td className="px-3 py-2 text-center">{org.brands}</td><td className="px-3 py-2 text-center">{org.content}</td><td className="px-3 py-2 text-center">{org.members}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <h3 className="font-display font-bold mb-4">Estado dos Providers</h3>
          <div className="space-y-2">
            {o?.providers?.map((p) => (
              <div key={p.provider} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2" data-testid={`provider-${p.provider}`}>
                <div><div className="text-sm font-medium text-slate-800">{p.capability}</div><div className="text-[11px] text-slate-400">{p.detail}</div></div>
                <ModeBadge mode={p.mode} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card>
          <h3 className="font-display font-bold mb-4">Planos configuráveis</h3>
          <div className="space-y-2">
            {o?.plans?.map((pl) => (
              <div key={pl.id} className="rounded-lg border border-slate-100 px-3 py-2.5" data-testid={`plan-${pl.name}`}>
                <div className="flex items-center justify-between"><span className="font-semibold text-slate-800">{pl.name}</span><span className="text-[11px] text-slate-400">{pl.brands_limit === 0 ? "marcas ilimitadas" : `${pl.brands_limit} marcas`}</span></div>
                <div className="text-xs text-slate-500 mt-1">{(pl.features || []).join(" · ")}</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Preços não são fixados no código — configuráveis por plano.</p>
        </Card>

        <Card className="p-0 overflow-hidden">
          <div className="p-5 pb-3"><h3 className="font-display font-bold">Logs & Auditoria</h3></div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {audit.length ? audit.map((l) => (
              <div key={l.id} className="px-5 py-2 text-xs flex items-center justify-between" data-testid={`audit-${l.id}`}>
                <span className="text-slate-600"><b className="text-slate-800">{l.action}</b> · {l.entity}</span>
                <span className="text-slate-400 mono">{(l.timestamp || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            )) : <div className="px-5 py-6 text-sm text-slate-400">Sem eventos.</div>}
          </div>
        </Card>
      </div>

      {/* Zona de Perigo */}
      <Card className="mt-6 border-2 border-rose-200 bg-rose-50/40" data-testid="danger-zone">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 text-rose-600"><AlertTriangle size={18} /></div>
            <div>
              <h3 className="font-display font-bold text-rose-700">Zona de Perigo — Zerar sistema</h3>
              <p className="mt-1 max-w-xl text-sm text-slate-600">
                Apaga <b>todas</b> as organizações, marcas, conteúdos, mídias, conexões e usuários,
                mantendo apenas a sua conta Super Admin. Use para limpar dados de teste. Esta ação é irreversível.
              </p>
            </div>
          </div>
          <Btn variant="danger" data-testid="open-reset-btn" onClick={() => setShowReset(true)}>Zerar sistema</Btn>
        </div>
      </Card>

      {showReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="reset-modal" onClick={() => !resetting && setShowReset(false)}>
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-rose-600"><AlertTriangle size={20} /><h3 className="font-display text-lg font-extrabold">Confirmar limpeza total</h3></div>
            <p className="mt-2 text-sm text-slate-600">
              Isso vai apagar permanentemente <b>{o?.orgs ?? 0}</b> organizações, <b>{o?.brands ?? 0}</b> marcas e <b>{o?.content ?? 0}</b> conteúdos.
              Sua conta Super Admin será mantida. Para confirmar, digite <b>ZERAR</b> abaixo.
            </p>
            <div className="mt-4">
              <Input value={confirmText} data-testid="reset-confirm-input" placeholder="Digite ZERAR" onChange={(e) => setConfirmText(e.target.value.toUpperCase())} />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="ghost" data-testid="reset-cancel-btn" disabled={resetting} onClick={() => setShowReset(false)}>Cancelar</Btn>
              <Btn variant="danger" data-testid="reset-confirm-btn" disabled={resetting || confirmText !== "ZERAR"} onClick={runReset}>
                {resetting ? "Zerando..." : "Zerar agora"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
